import { useState, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Header from 'components/header/header';
import HeaderSkeleton from 'components/header/headerSkeleton';
import SubHeader from 'components/subHeader/subHeader';
import MobileFooter from 'components/mobileFooter/mobileFooter';
import MobileMenu from 'components/mobileMenu/mobileMenu';
import Footer from 'components/footer/footer';
import Login from 'components/login/login';
import Modal from 'components/modal/modal';
import Register from 'components/register/register';
import VipClub from 'components/rewards/rewards';
import Settings from 'components/settings/settings';
import { Outlet, useSearchParams, useLocation } from 'react-router-dom';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { getUnlocalizedPath } from 'utils/localizedRouting';
import SearchBar from 'components/searchBar/searchBar';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { logoutUser } from 'store/authSlice';
import { setWalletOpen, setRewardsOpen, setLoginOpen, setRegisterOpen } from 'store/uiSlice';
import { lazy, Suspense } from 'react';
import { usePrefetch } from 'hooks/usePrefetch';
import styles from './layout.module.scss';

const WalletModal = lazy(() => import('components/walletModal/walletModal'));
const GlobalBetDetailsModal = lazy(
  () => import('components/betDetailsModal/GlobalBetDetailsModal'),
);

interface LayoutProps {
  children?: ReactNode;
}

const Layout: FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  usePrefetch();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isInitializing, isLoading } = useAppSelector((state) => state.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const {
    isWalletOpen,
    isRewardsOpen,
    isLoginOpen,
    isRegisterOpen,
    isBetDetailsOpen,
    isLoading: isGlobalLoading,
  } = useAppSelector((state) => state.ui);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const isBetModalInUrl =
    location.search.includes('modal=bet') ||
    location.pathname.includes('modal=bet') ||
    location.hash.includes('modal=bet');
  const isBetDetailsActive = isBetDetailsOpen || isBetModalInUrl;

  const currentPath = getUnlocalizedPath(location.pathname);
  const isSportsPage = currentPath.startsWith('/sports') || currentPath.startsWith('/sport');
  const activeTab = isRewardsOpen ? 'VIP' : isSportsPage ? 'Sports' : 'Casino';

  const onLoginSuccess = () => {
    dispatch(setLoginOpen(false));
    setIsModalLoading(false);
  };

  const referralCode = searchParams.get('r');

  useState(() => {
    if (referralCode && !isAuthenticated) {
      dispatch(setRegisterOpen(true));
    }
  });

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  return (
    <div className={styles.layout}>
      {isInitializing ? (
        <HeaderSkeleton />
      ) : (
        <Header
          isLoggedIn={isAuthenticated}
          onLoginClick={() => dispatch(setLoginOpen(true))}
          onRegisterClick={() => dispatch(setRegisterOpen(true))}
          onWalletClick={() => dispatch(setWalletOpen(true))}
          onVipClick={() => dispatch(setRewardsOpen(true))}
          onLogout={handleLogout}
        />
      )}
      {!isGlobalLoading ? (
        <div className={styles.contentFade}>
          {!isSportsPage && <SubHeader />}
          {isSearchOpen && (
            <div className={styles.searchOverlay}>
              <SearchBar onClose={() => setIsSearchOpen(false)} />
            </div>
          )}

          <main className={styles.mainContent}>{children || <Outlet />}</main>

          <Footer />

          <MobileFooter
            onMenuClick={() => setIsMenuOpen(true)}
            activeItem={activeTab}
            onVipClick={() => dispatch(setRewardsOpen(true))}
          />

          <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
        </div>
      ) : (
        <div style={{ flex: 1 }} />
      )}

      {isLoginOpen && (
        <Modal
          isOpen={isLoginOpen}
          onClose={() => {
            dispatch(setLoginOpen(false));
            setIsModalLoading(false);
          }}
          title={t('auth.loginTitle', t('common.login', 'Login'))}
          closeOnOverlayClick={false}
          hideCloseButton={isLoading}
        >
          <Login
            onClose={() => {
              dispatch(setLoginOpen(false));
              setIsModalLoading(false);
            }}
            onRegisterClick={() => {
              dispatch(setLoginOpen(false));
              dispatch(setRegisterOpen(true));
              setIsModalLoading(false);
            }}
            onLoginSuccess={onLoginSuccess}
            onLoadingChange={setIsModalLoading}
          />
        </Modal>
      )}

      {isRegisterOpen && (
        <Modal
          isOpen={isRegisterOpen}
          onClose={() => {
            dispatch(setRegisterOpen(false));
            setIsModalLoading(false);
          }}
          title={t('auth.registerTitle', 'Create an Account')}
          closeOnOverlayClick={false}
          hideCloseButton={isLoading}
        >
          <Register
            onClose={() => {
              dispatch(setRegisterOpen(false));
              setIsModalLoading(false);
            }}
            onLoginClick={() => {
              dispatch(setRegisterOpen(false));
              dispatch(setLoginOpen(true));
              setIsModalLoading(false);
            }}
            referralFromUrl={referralCode || undefined}
            onLoadingChange={setIsModalLoading}
          />
        </Modal>
      )}

      {isWalletOpen && (
        <Suspense fallback={null}>
          <WalletModal
            isOpen={isWalletOpen}
            onClose={() => dispatch(setWalletOpen(false))}
            onOpenSettings={() => setIsSettingsOpen(true)}
            key={isWalletOpen ? 'opened' : 'closed'}
          />
        </Suspense>
      )}
      {isRewardsOpen && (
        <VipClub isOpen={isRewardsOpen} onClose={() => dispatch(setRewardsOpen(false))} />
      )}
      {isSettingsOpen && (
        <Modal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          title="Settings"
          className={styles.layoutModal}
        >
          <Settings />
        </Modal>
      )}
      {isBetDetailsActive && (
        <Suspense fallback={null}>
          <GlobalBetDetailsModal />
        </Suspense>
      )}
    </div>
  );
};

export default Layout;
